import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api-client';
import { formatMoney } from '../lib/money';
import { apiError } from '../lib/utils';
import { useNotice } from '../components/ui/Notice';
import { Button } from '../components/ui/Button';
import { PageHead } from '../components/ui/chrome';
import { Alert } from '../components/ui/Badge';

interface ReviewRow {
  id: string;
  kind: 'payment_match' | 'bill_category';
  payment?: { description: string; amountPence: number; paidOn: string };
  invoice?: { number: string; customerName: string; outstandingPence: number };
  bill?: { number: string; amountPence: number; expenseType: string };
}

export function ReviewPage() {
  const notice = useNotice();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['review'],
    queryFn: () => apiGet<ReviewRow[]>('/api/review'),
  });

  async function decide(id: string, action: 'accept' | 'reject') {
    await notice.run(action === 'accept' ? 'Accepting' : 'Rejecting', async () => {
      await apiPost(`/api/review/${id}/${action}`, {});
      await queryClient.invalidateQueries({ queryKey: ['review'] });
    }, action === 'accept' ? 'Accepted' : 'Rejected');
  }

  const rows = query.data || [];
  return (
    <div>
      <PageHead crumbs={[{ label: 'Overview' }, { label: 'Review' }]} title="Review" subtitle="Items assigned to you." />
      {query.error ? <Alert error>{apiError(query.error, 'Could not load review')}</Alert> : null}
      {!rows.length ? <p className="text-sm text-[var(--bo-muted)]">{query.isLoading ? 'Loading' : 'Nothing is assigned to you.'}</p> : null}
      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.id} className="rounded-lg border border-[var(--bo-border)] bg-[var(--bo-surface)] p-4">
            {row.kind === 'payment_match' ? (
              <div>
                <div className="font-medium">Payment {formatMoney(row.payment?.amountPence)} on {row.payment?.paidOn}</div>
                <div className="text-sm text-[var(--bo-muted)]">{row.payment?.description || 'No description'}</div>
                <div className="mt-2 text-sm">Proposed invoice {row.invoice?.number} {row.invoice?.customerName ? `for ${row.invoice.customerName}` : ''}. Outstanding {formatMoney(row.invoice?.outstandingPence)}.</div>
              </div>
            ) : (
              <div>
                <div className="font-medium">Bill {row.bill?.number} {formatMoney(row.bill?.amountPence)}</div>
                <div className="mt-2 text-sm">Proposed category {row.bill?.expenseType}</div>
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <Button onClick={() => void decide(row.id, 'accept')}>Accept</Button>
              <Button variant="outline" onClick={() => void decide(row.id, 'reject')}>Reject</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
